import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Calendar,
  Clock,
  ArrowLeft,
  MessageCircle,
  ChevronRight,
} from "lucide-react";
import { KENTRO } from "@/lib/constants";

const SITE = "https://kentro.com.br";
const URL = `${SITE}/fortalecendo-a-cultura-empresarial-atraves-da-gestao-de-atendimento`;
const TITLE =
  "Fortalecendo a cultura empresarial através da gestão de atendimento";
const DESCRIPTION =
  "Como a gestão de atendimento molda a cultura empresarial: rituais, métricas, IA omnichannel e o impacto direto na experiência do cliente e na retenção de talentos.";
const KEYWORD = "cultura empresarial gestão de atendimento";
const CATEGORY = "Cultura & Gestão";
const AUTHOR = "Equipe Kentro";
const DATE_ISO = "2024-09-12";
const READING_MIN = 8;

const FAQ = [
  {
    q: "O que a gestão de atendimento tem a ver com cultura empresarial?",
    a: "Tudo. A forma como uma empresa atende reflete os valores que ela realmente pratica — não os que estão no manual. Processos claros, métricas justas e ferramentas de qualidade transformam discurso em comportamento diário.",
  },
  {
    q: "Como medir o impacto da cultura no atendimento?",
    a: "Combine indicadores duros (TMA, TMR, FCR, CSAT, NPS) com indicadores de cultura (eNPS, turnover do time de CX, % de feedbacks aplicados). Quando os dois sobem juntos, a cultura está virando produto.",
  },
  {
    q: "Por onde começar para fortalecer a cultura via atendimento?",
    a: "Defina 3 a 5 princípios de atendimento, conecte cada princípio a um indicador, centralize os canais em uma plataforma omnichannel e crie rituais semanais de revisão de conversas reais com o time.",
  },
];

export const Route = createFileRoute(
  "/fortalecendo-a-cultura-empresarial-atraves-da-gestao-de-atendimento",
)({
  head: () => {
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: TITLE,
      description: DESCRIPTION,
      image: [`${SITE}/og-image.png`],
      datePublished: DATE_ISO,
      dateModified: DATE_ISO,
      inLanguage: "pt-BR",
      author: { "@type": "Organization", name: AUTHOR, url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Kentro Sistemas",
        logo: {
          "@type": "ImageObject",
          url: `${SITE}/og-image.png`,
        },
      },
      mainEntityOfPage: URL,
      keywords: KEYWORD,
      articleSection: CATEGORY,
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: TITLE, item: URL },
      ],
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    return {
      meta: [
        { title: `${TITLE} | Kentro` },
        { name: "description", content: DESCRIPTION },
        { name: "keywords", content: KEYWORD },
        { property: "og:type", content: "article" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:image", content: `${SITE}/og-image.png` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: `${SITE}/og-image.png` },
        { name: "article:published_time", content: DATE_ISO },
        { name: "article:section", content: CATEGORY },
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
        { type: "application/ld+json", children: JSON.stringify(faqLd) },
      ],
    };
  },
  component: ArticlePage,
});

function ArticlePage() {
  const formattedDate = new Date(DATE_ISO).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <article className="mx-auto max-w-3xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
      <nav
        aria-label="Breadcrumb"
        className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
      >
        <Link to="/" className="hover:text-foreground">
          Início
        </Link>
        <ChevronRight className="size-3" />
        <Link to="/blog" className="hover:text-foreground">
          Blog
        </Link>
        <ChevronRight className="size-3" />
        <span className="text-foreground/80">{CATEGORY}</span>
      </nav>

      <div className="mt-6 rounded-2xl border border-border bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-10">
        <header>
          <span className="inline-flex rounded-full border border-border bg-background/60 px-2.5 py-1 text-xs text-muted-foreground">
            {CATEGORY}
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{DESCRIPTION}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="size-4" /> {formattedDate}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-4" /> {READING_MIN} min de leitura
            </span>
            <span>Por {AUTHOR}</span>
          </div>
        </header>

        <div className="prose mt-10 max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90 prose-a:text-primary-glow hover:prose-a:underline prose-blockquote:text-foreground/80 prose-blockquote:border-l-primary-glow prose-code:text-foreground prose-hr:border-border dark:prose-invert">
          <p>
            Cultura não é o pôster na parede — é o que acontece quando um cliente
            grita no WhatsApp às 19h47 de uma sexta-feira. É nesse momento que a
            empresa real aparece: o tom de voz que o atendente usa, o tempo que
            ele leva para responder, a autonomia que ele tem para resolver, a
            forma como o líder vai tratar o caso na segunda. Por isso a{" "}
            <strong>gestão de atendimento</strong> é, na prática, uma das
            ferramentas mais poderosas para construir e fortalecer a{" "}
            <strong>cultura empresarial</strong>.
          </p>

          <h2 id="atendimento-como-espelho">
            Atendimento é o espelho mais honesto da cultura
          </h2>
          <p>
            Toda empresa tem dois conjuntos de valores: os declarados (missão,
            visão, manifesto) e os praticados (o que acontece quando ninguém
            está olhando). O atendimento é o ponto onde os dois se encontram —
            e onde a distância entre eles fica evidente. Um cliente percebe em
            30 segundos se a empresa que diz "colocar o cliente no centro"
            realmente faz isso ou só repete o slogan.
          </p>
          <p>
            Times de atendimento bem geridos não nascem de discurso. Nascem de{" "}
            <strong>processos claros, ferramentas adequadas, métricas justas e
            lideranças presentes</strong>. Quando esses quatro pilares estão no
            lugar, a cultura para de ser um documento e vira comportamento
            diário.
          </p>

          <h2 id="pilares">Os 4 pilares que conectam cultura e atendimento</h2>

          <h3>1. Princípios de atendimento traduzidos em comportamento</h3>
          <p>
            Em vez de cinco valores genéricos, defina de 3 a 5{" "}
            <strong>princípios de atendimento</strong> específicos — "responder
            em até X minutos", "nunca devolver o cliente para outro setor sem
            contexto", "encerrar com um próximo passo combinado". Princípios são
            mais úteis que valores porque podem ser observados em uma conversa
            real.
          </p>

          <h3>2. Métricas que medem o que importa</h3>
          <p>
            Métricas moldam comportamento. Se a empresa só mede{" "}
            <strong>TMA (tempo médio de atendimento)</strong>, o time aprende a
            ser rápido — mesmo que isso signifique despachar o cliente. Combine:
          </p>
          <ul>
            <li>
              <strong>Indicadores de eficiência:</strong> TMA, TMR (tempo médio
              de resposta), TME (tempo médio de espera), FCR (first contact
              resolution).
            </li>
            <li>
              <strong>Indicadores de qualidade:</strong> CSAT, NPS, taxa de
              reabertura, conformidade com o tom de voz da marca.
            </li>
            <li>
              <strong>Indicadores de cultura:</strong> eNPS do time, turnover de
              CX, % de feedbacks de cliente que viraram melhoria.
            </li>
          </ul>
          <p>
            A regra simples: nenhum indicador de eficiência pode subir às custas
            de um indicador de qualidade. Quando isso acontece, a cultura está
            sendo silenciosamente vendida.
          </p>

          <h3>3. Tecnologia que serve o time, não o contrário</h3>
          <p>
            Atendente que pula entre 6 telas para responder uma mensagem aprende
            que a empresa não confia nele. Centralizar WhatsApp, Instagram,
            e-mail, webchat e voz em uma{" "}
            <strong>plataforma omnichannel com CRM e IA</strong> não é luxo — é
            respeito pelo tempo de quem atende. Quando a IA cuida do repetitivo
            (triagem, FAQ, pós-venda básico), o humano sobra para o que pede
            empatia, julgamento e criatividade. É aí que a cultura aparece.
          </p>

          <h3>4. Rituais de liderança ao redor de conversas reais</h3>
          <p>
            O ritual mais poderoso que vimos em empresas com forte cultura de
            atendimento é simples:{" "}
            <strong>uma reunião semanal de 30 minutos</strong> onde o time
            escuta junto 2 ou 3 atendimentos reais — um excelente, um difícil,
            um que poderia ter sido melhor. Sem caça às bruxas, com curiosidade.
            Em poucas semanas, o padrão se eleva sozinho, porque todo mundo viu
            o que "bom" significa na prática.
          </p>

          <h2 id="impacto">
            O impacto concreto: cliente fiel, time engajado, marca forte
          </h2>
          <p>
            Quando atendimento e cultura andam juntos, três coisas acontecem em
            paralelo:
          </p>
          <ul>
            <li>
              <strong>Clientes ficam mais tempo.</strong> Retenção sobe porque a
              experiência é coerente em todos os canais e horários.
            </li>
            <li>
              <strong>Talentos ficam mais tempo.</strong> Atendentes percebem que
              têm processo, tecnologia e voz — e param de pedir demissão como
              forma de protesto silencioso.
            </li>
            <li>
              <strong>Marca vira boca a boca.</strong> Os melhores embaixadores
              da empresa não estão no marketing: estão na fila de atendimento,
              respondendo bem todos os dias.
            </li>
          </ul>

          <h2 id="plano-30-dias">
            Plano de 30 dias para começar hoje
          </h2>
          <ul>
            <li>
              <strong>Semana 1:</strong> mapeie todos os canais ativos e o
              volume real por canal. Centralize a operação em uma única
              plataforma omnichannel.
            </li>
            <li>
              <strong>Semana 2:</strong> escreva 3 a 5 princípios de
              atendimento. Conecte cada princípio a um indicador medível.
            </li>
            <li>
              <strong>Semana 3:</strong> ative IA para triagem, FAQ e
              pós-atendimento básico. Libere o time humano para os casos
              complexos.
            </li>
            <li>
              <strong>Semana 4:</strong> instale o ritual semanal de escuta de
              conversas reais com o time. Comece pelo positivo — o que está
              dando certo merece ser amplificado.
            </li>
          </ul>

          <h2 id="conclusao">Conclusão</h2>
          <p>
            Cultura empresarial não se fortalece com palestras motivacionais.
            Fortalece-se quando processos, métricas, tecnologia e liderança
            trabalham juntos para que o atendimento seja a expressão diária dos
            valores da empresa. <strong>Quem cuida bem do atendimento, cuida
            bem da cultura</strong> — e o oposto também é verdadeiro.
          </p>
          <p>
            Quer ver como a Kentro ajuda empresas brasileiras a transformar
            atendimento em vantagem cultural?{" "}
            <Link to="/gestao-de-atendimento" className="text-primary-glow hover:underline">
              Conheça a plataforma de Gestão de Atendimento
            </Link>{" "}
            ou{" "}
            <Link to="/contato" className="text-primary-glow hover:underline">
              fale com um especialista
            </Link>
            .
          </p>
        </div>
      </div>

      <section className="mt-12">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          Perguntas frequentes
        </h2>
        <div className="mt-5 space-y-3">
          {FAQ.map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card/60"
            >
              <summary className="cursor-pointer list-none font-semibold">
                {f.q}
                <span className="float-right text-muted-foreground group-open:hidden">
                  +
                </span>
                <span className="float-right hidden text-muted-foreground group-open:inline">
                  −
                </span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-card/40 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Continue lendo
        </h2>
        <ul className="mt-3 space-y-1.5 text-sm">
          <li>
            <Link
              to="/gestao-de-atendimento"
              className="text-primary-glow hover:underline"
            >
              Gestão de Atendimento Kentro →
            </Link>
          </li>
          <li>
            <Link to="/omnichannel" className="text-primary-glow hover:underline">
              Plataforma Omnichannel →
            </Link>
          </li>
          <li>
            <Link to="/ia" className="text-primary-glow hover:underline">
              IA aplicada ao atendimento →
            </Link>
          </li>
          <li>
            <Link to="/blog" className="text-primary-glow hover:underline">
              Mais artigos no Kentro News →
            </Link>
          </li>
        </ul>
      </section>

      <section className="gradient-primary shadow-glow mt-12 rounded-2xl p-6 text-white md:p-8">
        <h2 className="text-xl font-bold md:text-2xl">
          Pronto para evoluir seu atendimento?
        </h2>
        <p className="mt-2 text-white/85">
          Fale com um especialista da Kentro e veja a plataforma rodando com
          seus próprios fluxos.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/contato"
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90"
          >
            Solicitar demonstração
          </Link>
          <a
            href={KENTRO.whatsappComercialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
          >
            <MessageCircle className="size-4" /> WhatsApp (38) 3220-8706
          </a>
        </div>
      </section>

      <div className="mt-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Voltar ao blog
        </Link>
      </div>
    </article>
  );
}
